import { faFileAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useNotification from 'hooks/useNotification';
import { useEffect, useRef, useState } from 'react';
import MessageTemplateService from 'services/MessageTemplate.service';

/**
 * Componente de autocomplete para modelos de mensagem no editor
 * Detecta atalho de teclado e mostra sugestões de modelos filtrados
 */
const MessageTemplateAutocomplete = ({
  editorRef,
  type,
  ticketId,
  onTemplateApplied
}) => {
  const { showErrorNotification } = useNotification();
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [autocompletePosition, setAutocompletePosition] = useState({
    top: 0,
    left: 0
  });
  const autocompleteRef = useRef(null);
  const isKeyboardNavigation = useRef(false);
  const isInitialized = useRef(false);

  // Carrega templates uma vez
  useEffect(() => {
    if (!isInitialized.current && showAutocomplete) {
      loadTemplates();
      isInitialized.current = true;
    }
  }, [showAutocomplete]);

  const loadTemplates = async () => {
    try {
      const params = new URLSearchParams();
      params.append('types', type);
      params.append('size', '100'); // Carrega todos os modelos do tipo

      const response = await MessageTemplateService.list(params.toString());

      if (response.content) {
        setTemplates(response.content);
        setFilteredTemplates(response.content);
      } else {
        setTemplates([]);
        setFilteredTemplates([]);
      }
    } catch (error) {
      console.error('Erro ao carregar modelos de mensagem:', error);
      showErrorNotification('Erro ao carregar modelos de mensagem');
      setTemplates([]);
      setFilteredTemplates([]);
    }
  };

  // Filtra templates baseado no termo de busca
  useEffect(() => {
    if (!searchTerm) {
      setFilteredTemplates(templates);
      setSelectedIndex(0);
      return;
    }

    const filtered = templates.filter(template =>
      template.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredTemplates(filtered);
    setSelectedIndex(0);
    setHoveredIndex(null);
  }, [searchTerm, templates]);

  // Detecta atalho de teclado Ctrl+M ou Ctrl+Shift+M
  useEffect(() => {
    if (!editorRef?.current) return;

    const getQuill = () => {
      if (editorRef.current?.getQuill) {
        return editorRef.current.getQuill();
      }
      const reactQuill = editorRef.current;
      return reactQuill?.getEditor?.();
    };

    const quill = getQuill();
    if (!quill) return;

    // Obtém o elemento .ql-editor específico deste editor
    const getEditorElement = () => {
      const quillRoot = quill.root;
      return quillRoot;
    };

    const handleKeyDown = e => {
      // Verifica se Ctrl+M foi pressionado (mas não Ctrl+Shift+M que pode ser outro atalho)
      if (e.ctrlKey && !e.shiftKey && (e.key === 'm' || e.key === 'M')) {
        // Verifica se o editor específico está focado
        const editorElement = getEditorElement();
        if (editorElement && editorElement.contains(document.activeElement)) {
          e.preventDefault();
          e.stopPropagation();

          const range = quill.getSelection(false);
          if (!range) {
            quill.focus();
            const newRange = quill.getSelection(true);
            if (newRange) {
              showAutocompleteAtPosition(newRange.index);
            }
          } else {
            showAutocompleteAtPosition(range.index);
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [editorRef]);

  const autocompleteStartIndexRef = useRef(0);
  const isAutocompleteActiveRef = useRef(false);

  // Detecta quando o usuário digita após abrir o autocomplete
  useEffect(() => {
    if (!showAutocomplete || !editorRef?.current) {
      isAutocompleteActiveRef.current = false;
      return;
    }

    isAutocompleteActiveRef.current = true;

    const getQuill = () => {
      if (editorRef.current?.getQuill) {
        return editorRef.current.getQuill();
      }
      const reactQuill = editorRef.current;
      return reactQuill?.getEditor?.();
    };

    const quill = getQuill();
    if (!quill) return;

    const handleTextChange = () => {
      if (!isAutocompleteActiveRef.current) return;

      const range = quill.getSelection(false);
      if (!range) {
        setShowAutocomplete(false);
        isAutocompleteActiveRef.current = false;
        return;
      }

      // Obtém o texto desde a posição inicial até o cursor
      const currentIndex = range.index;
      const startIndex = autocompleteStartIndexRef.current;

      if (currentIndex >= startIndex) {
        const text = quill.getText(startIndex, currentIndex);
        setSearchTerm(text);
      } else {
        // Se o cursor voltou antes da posição inicial, fecha o autocomplete
        setShowAutocomplete(false);
        isAutocompleteActiveRef.current = false;
      }
    };

    quill.on('text-change', handleTextChange);

    return () => {
      quill.off('text-change', handleTextChange);
      isAutocompleteActiveRef.current = false;
    };
  }, [showAutocomplete, editorRef]);

  const showAutocompleteAtPosition = index => {
    const quill =
      editorRef.current?.getQuill?.() || editorRef.current?.getEditor?.();
    if (quill) {
      const editorElement = quill.root;
      const bounds = quill.getBounds(index);
      const editorRect = editorElement.getBoundingClientRect();
      setAutocompletePosition({
        top: editorRect.top + bounds.top + bounds.height + 5,
        left: editorRect.left + bounds.left
      });
      autocompleteStartIndexRef.current = index;
      setShowAutocomplete(true);
      setSearchTerm('');
      setSelectedIndex(0);
      setHoveredIndex(null);
      isKeyboardNavigation.current = false;
    }
  };

  // Handler para aplicar template selecionado
  const applyTemplate = async template => {
    if (!editorRef?.current) return;

    const getQuill = () => {
      if (editorRef.current?.getQuill) {
        return editorRef.current.getQuill();
      }
      const reactQuill = editorRef.current;
      return reactQuill?.getEditor?.();
    };

    const quill = getQuill();
    if (!quill) return;

    try {
      const response = await MessageTemplateService.apply(
        template.id,
        ticketId
      );

      if (response.evaluatedResponse !== undefined) {
        const range = quill.getSelection(true);
        const currentIndex = range
          ? range.index
          : autocompleteStartIndexRef.current;
        const startIndex = autocompleteStartIndexRef.current;

        // Remove o texto de busca se houver
        if (searchTerm && currentIndex > startIndex) {
          quill.deleteText(startIndex, currentIndex - startIndex);
        }

        const content = response.evaluatedResponse;

        // Guarda o tamanho antes de inserir
        const lengthBefore = quill.getLength();

        // Insere o HTML na posição do cursor
        quill.clipboard.dangerouslyPasteHTML(startIndex, content, 'user');

        // Calcula o tamanho inserido e posiciona o cursor
        const lengthAfter = quill.getLength();
        const insertedLength = lengthAfter - lengthBefore;
        quill.setSelection(startIndex + insertedLength);

        if (onTemplateApplied) {
          // Retorna o HTML completo do editor para manter o state sincronizado
          onTemplateApplied(quill.root.innerHTML);
        }
      }
    } catch (error) {
      console.error('Erro ao aplicar modelo de mensagem:', error);
      showErrorNotification('Erro ao aplicar modelo de mensagem');
    }

    setShowAutocomplete(false);
    setHoveredIndex(null);
    isKeyboardNavigation.current = false;
  };

  // Handler para navegação com teclado
  useEffect(() => {
    if (!showAutocomplete) return;

    const getQuill = () => {
      if (editorRef.current?.getQuill) {
        return editorRef.current.getQuill();
      }
      const reactQuill = editorRef.current;
      return reactQuill?.getEditor?.();
    };

    const handleKeyDown = e => {
      // Verifica se o editor específico está focado antes de processar
      const quill = getQuill();
      const editorElement = quill?.root;
      if (!editorElement || !editorElement.contains(document.activeElement)) {
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        isKeyboardNavigation.current = true;
        setHoveredIndex(null);
        setSelectedIndex(prev =>
          prev < filteredTemplates.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        isKeyboardNavigation.current = true;
        setHoveredIndex(null);
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0));
      } else if (e.key === 'Enter' && filteredTemplates[selectedIndex]) {
        e.preventDefault();
        e.stopPropagation();
        applyTemplate(filteredTemplates[selectedIndex]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setShowAutocomplete(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [showAutocomplete, filteredTemplates, selectedIndex, editorRef]);

  // Fecha autocomplete ao clicar fora
  useEffect(() => {
    if (!showAutocomplete) return;

    const getQuill = () => {
      if (editorRef.current?.getQuill) {
        return editorRef.current.getQuill();
      }
      const reactQuill = editorRef.current;
      return reactQuill?.getEditor?.();
    };

    const handleClickOutside = e => {
      const quill = getQuill();
      const editorElement = quill?.root;

      if (
        autocompleteRef.current &&
        !autocompleteRef.current.contains(e.target) &&
        !editorElement?.contains(e.target)
      ) {
        setShowAutocomplete(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAutocomplete, editorRef]);

  if (!showAutocomplete || filteredTemplates.length === 0) {
    return null;
  }

  return (
    <div
      ref={autocompleteRef}
      className="position-fixed bg-body border shadow-lg rounded"
      style={{
        top: `${autocompletePosition.top}px`,
        left: `${autocompletePosition.left}px`,
        zIndex: 9999,
        minWidth: '300px',
        maxWidth: '400px',
        maxHeight: '300px',
        overflowY: 'auto'
      }}
    >
      <div className="p-2 border-bottom">
        <small className="text-muted d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faFileAlt} size="sm" />
          <span>Modelos disponíveis</span>
        </small>
      </div>
      <div className="d-flex flex-column">
        {filteredTemplates.map((template, index) => {
          const isActive =
            hoveredIndex === index ||
            (hoveredIndex === null &&
              isKeyboardNavigation.current &&
              index === selectedIndex);

          return (
            <button
              key={template.id}
              type="button"
              className={`text-start border-0 p-2 ${
                isActive ? 'bg-body-secondary' : 'bg-transparent'
              }`}
              onClick={() => applyTemplate(template)}
              onMouseEnter={() => {
                isKeyboardNavigation.current = false;
                setHoveredIndex(index);
              }}
              onMouseLeave={() => {
                setHoveredIndex(null);
              }}
              style={{
                cursor: 'pointer',
                transition: 'background-color 0.15s'
              }}
            >
              <div
                className={`small fw-medium ${isActive ? 'text-muted' : ''}`}
              >
                {template.name}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MessageTemplateAutocomplete;
